import React, { useRef, useEffect, useCallback } from "react";
import { Bold, Italic, List, ListOrdered, Link2, Image as ImageIcon, Quote, Heading2, Heading3, Pilcrow } from "lucide-react";

// Editor rich-text leggero, zero-dipendenze (contentEditable + document.execCommand).
// Il contenuto HTML viene sanitizzato lato backend al salvataggio.
export const RichTextEditor = ({ value = "", onChange, testid = "rte" }) => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Sincronizza il DOM quando il valore cambia dall'esterno (es. caricamento articolo),
    // ma NON mentre l'utente sta scrivendo (editor a fuoco) per non spostare il cursore.
    if (document.activeElement !== el && el.innerHTML !== (value || "")) {
      el.innerHTML = value || "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const emit = useCallback(() => { if (ref.current && onChange) onChange(ref.current.innerHTML); }, [onChange]);

  const escapeHtml = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  // Converte testo semplice in HTML preservando paragrafi, a-capo e liste (•/- e 1.).
  const plainTextToHtml = (text) => {
    const blocks = text.replace(/\r\n?/g, "\n").split(/\n{2,}/);
    const isBullet = (l) => /^[•▪◦·*\-–]\s+/.test(l);
    const stripBullet = (l) => l.replace(/^[•▪◦·*\-–]\s+/, "");
    const out = [];
    for (const block of blocks) {
      const lines = block.split("\n").map((l) => l.trim()).filter(Boolean);
      if (!lines.length) continue;
      let i = 0;
      while (i < lines.length) {
        if (isBullet(lines[i])) {
          const items = [];
          while (i < lines.length && isBullet(lines[i])) { items.push(`<li>${escapeHtml(stripBullet(lines[i]))}</li>`); i++; }
          out.push(`<ul>${items.join("")}</ul>`);
        } else {
          out.push(`<p>${escapeHtml(lines[i])}</p>`);
          i++;
        }
      }
    }
    return out.join("");
  };

  // Pulisce l'HTML incollato (Word/browser): rimuove stili/classi, tag Office,
  // e soprattutto scioglie i <p>/<div> annidati dentro i <li> (causa degli spazi enormi).
  const cleanPastedHtml = (html) => {
    try {
      const doc = new DOMParser().parseFromString(html, "text/html");
      const body = doc.body;
      body.querySelectorAll("*").forEach((el) => {
        el.removeAttribute("style"); el.removeAttribute("class");
        el.removeAttribute("lang"); el.removeAttribute("dir"); el.removeAttribute("align");
        if (/^(o|w|st1|v|m):/i.test(el.tagName)) el.replaceWith(...el.childNodes);
      });
      // Scioglie i blocchi dentro i <li>: <li><p>testo</p></li> -> <li>testo</li>
      body.querySelectorAll("li").forEach((li) => {
        li.querySelectorAll("p,div").forEach((b) => {
          const frag = doc.createDocumentFragment();
          while (b.firstChild) frag.appendChild(b.firstChild);
          b.replaceWith(frag);
        });
        li.innerHTML = li.innerHTML.replace(/^(?:\s|<br\s*\/?>)+/i, "").replace(/(?:\s|<br\s*\/?>)+$/i, "");
      });
      // <div> di primo livello -> <p>
      Array.from(body.children).forEach((el) => {
        if (el.tagName === "DIV") { const p = doc.createElement("p"); p.innerHTML = el.innerHTML; el.replaceWith(p); }
      });
      // Rimuove blocchi vuoti
      body.querySelectorAll("p,div,span,li,h2,h3").forEach((el) => {
        if (!el.textContent.trim() && !el.querySelector("img")) el.remove();
      });
      return body.innerHTML;
    } catch {
      return html;
    }
  };

  const handlePaste = (e) => {
    const cd = e.clipboardData || window.clipboardData;
    if (!cd) return;
    const html = cd.getData("text/html");
    const plain = cd.getData("text/plain");
    // Se arriva HTML valido (Word/browser) lo si pulisce e poi si sanitizza al salvataggio.
    if (html && /<\w+/.test(html)) {
      e.preventDefault();
      ref.current?.focus();
      document.execCommand("insertHTML", false, cleanPastedHtml(html));
      emit();
      return;
    }
    if (plain != null) {
      e.preventDefault();
      ref.current?.focus();
      document.execCommand("insertHTML", false, plainTextToHtml(plain));
      emit();
    }
  };

  const exec = (cmd, arg = null) => {
    ref.current?.focus();
    document.execCommand(cmd, false, arg);
    emit();
  };
  const block = (tag) => exec("formatBlock", tag);
  const addLink = () => { const url = window.prompt("URL del link (https://...)"); if (url) exec("createLink", url); };
  const addImage = () => { const url = window.prompt("URL immagine (puoi copiarlo dal Media Manager)"); if (url) exec("insertImage", url); };

  const Btn = ({ onClick, title, children, tid }) => (
    <button type="button" data-testid={`${testid}-${tid}`} onMouseDown={(e) => e.preventDefault()} onClick={onClick} title={title}
      className="h-8 w-8 rounded-md border border-brand-navy/15 flex items-center justify-center text-brand-navy/70 hover:border-brand-electric hover:text-brand-electric transition-colors">
      {children}
    </button>
  );

  return (
    <div className="rounded-xl border border-brand-navy/20 overflow-hidden bg-white">
      <div className="flex flex-wrap items-center gap-1 border-b border-brand-navy/10 bg-brand-light/60 p-2">
        <Btn tid="p" onClick={() => block("p")} title="Paragrafo"><Pilcrow className="h-4 w-4" /></Btn>
        <Btn tid="h2" onClick={() => block("h2")} title="Titolo H2"><Heading2 className="h-4 w-4" /></Btn>
        <Btn tid="h3" onClick={() => block("h3")} title="Titolo H3"><Heading3 className="h-4 w-4" /></Btn>
        <span className="w-px h-6 bg-brand-navy/10 mx-1" />
        <Btn tid="bold" onClick={() => exec("bold")} title="Grassetto"><Bold className="h-4 w-4" /></Btn>
        <Btn tid="italic" onClick={() => exec("italic")} title="Corsivo"><Italic className="h-4 w-4" /></Btn>
        <span className="w-px h-6 bg-brand-navy/10 mx-1" />
        <Btn tid="ul" onClick={() => exec("insertUnorderedList")} title="Elenco puntato"><List className="h-4 w-4" /></Btn>
        <Btn tid="ol" onClick={() => exec("insertOrderedList")} title="Elenco numerato"><ListOrdered className="h-4 w-4" /></Btn>
        <Btn tid="quote" onClick={() => block("blockquote")} title="Citazione"><Quote className="h-4 w-4" /></Btn>
        <span className="w-px h-6 bg-brand-navy/10 mx-1" />
        <Btn tid="link" onClick={addLink} title="Link"><Link2 className="h-4 w-4" /></Btn>
        <Btn tid="image" onClick={addImage} title="Immagine"><ImageIcon className="h-4 w-4" /></Btn>
      </div>
      <div
        ref={ref}
        data-testid={`${testid}-area`}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        onPaste={handlePaste}
        className="prose-editor min-h-[280px] max-h-[520px] overflow-y-auto p-4 text-brand-navy focus:outline-none text-[15px] leading-relaxed"
        style={{ whiteSpace: "pre-wrap" }}
      />
    </div>
  );
};

export default RichTextEditor;
